<template>
  <div class="location-editor">
    <div v-if="showPlaceSearch" class="location-editor__field">
      <label class="location-editor__search">
        <span v-if="props.placeSearchLabel" class="global-h4">{{ props.placeSearchLabel }}</span>
        <GMapAutocomplete
          ref="placeSearchInput"
          class="global-text-input"
          :class="{ 'global-text-input--search': Boolean(props.placeSearchLabel) }"
          placeholder="Search for a business or place"
          :options="{ fields: ['name', 'place_id', 'address_components', 'formatted_address', 'geometry', 'website', 'types'] }"
          @place_changed="onGooglePlaceSelected"
          @error="onAutocompleteError"
        />
      </label>
      <LinkWithIcon v-if="!isManualMode && !props.location" color="gray" isDottedUnderline @click="isManualMode = true" style="margin-top: 8px">
        <template #icon><IconEmbedded name="edit-pencil_2-5" :size="17" /></template>
        Manually Enter
      </LinkWithIcon>
    </div>

    <div v-if="showTagKindPicker" class="location-editor__kind" role="radiogroup" :aria-label="props.location ? 'Account type' : 'I\'m adding a'">
      <span v-if="!props.location" class="location-editor__kind-label">I'm adding</span>
      <div class="location-editor__kind-options">
        <button
          v-for="option in tagKindOptions"
          :key="option.value"
          type="button"
          role="radio"
          class="location-editor__kind-option"
          :class="{ 'location-editor__kind-option--selected': tagKind === option.value }"
          :aria-checked="tagKind === option.value"
          @click="tagKind = option.value"
        >
          <IconEmbedded :name="option.icon" :size="16" color="currentColor" />
          <span>{{ option.label }}</span>
        </button>
      </div>
    </div>

    <template v-if="showDetailFields">
      <div class="location-editor__identity">
        <div class="location-editor__avatar-wrap">
          <LocationAvatar :src="previewIcon" :name="name" :size="56" placeholderTone="gray" />
          <button v-if="isNameEditMode && previewIcon" type="button" class="location-editor__clear-icon" aria-label="Clear location icon" @mousedown.prevent @click="clearIcon">
            <IconEmbedded name="remove_2" :size="12" color="#667085" />
          </button>
        </div>
        <div class="location-editor__identity-body">
          <div class="location-editor__name-row">
            <div v-if="name.trim() && !isEditingName" class="location-editor__name-display">
              <span>{{ name }}</span>
              <button type="button" aria-label="Edit Location name" @click="beginNameEdit">
                <IconEmbedded name="edit-pencil_2-5" :size="17" />
              </button>
            </div>
            <label v-else class="location-editor__field location-editor__field--grow" aria-label="Location name">
              <input
                ref="nameInput"
                v-model="name"
                class="global-text-input"
                maxlength="150"
                :placeholder="namePlaceholder"
                @focus="isEditingName = true"
                @blur="finishNameEdit"
                @keydown.enter.prevent="finishNameEdit"
              />
            </label>
            <LocationCategoryPicker v-if="tagKind === 'Location'" v-model="category" />
          </div>
          <small v-if="selectedPlace?.address" class="location-editor__place-address">{{ selectedPlace.address }}</small>
          <a v-if="selectedPlace?.website" class="location-editor__url-link" :href="selectedPlace.website" target="_blank" rel="noopener noreferrer" @click.stop>
            {{ stripUrlProtocol(selectedPlace.website) }}
            <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
          </a>
        </div>
        <button v-if="selectedPlace" type="button" class="location-editor__fb-change" @click="changePlace">Change</button>
      </div>

      <p v-if="isDiscoveringSocials || discoveryMessage" class="location-editor__status-row location-editor__discovery-status">
        <LoadingIcon v-if="isDiscoveringSocials" :size="18" />
        {{ isDiscoveringSocials ? "Finding social accounts..." : discoveryMessage }}
      </p>

      <label class="location-editor__field location-editor__social-row" style="margin-top: 18px !important">
        <img class="location-editor__social-logo" src="/images/social-logo-instagram.svg" alt="Instagram" />
        <div class="location-editor__social-body">
          <div class="location-editor__handle-input">
            <span>@</span>
            <input v-model="instagramHandle" type="text" placeholder="username" @input="onInstagramInput" @blur="handleInstagramBlur" />
            <LoadingIcon v-if="isCheckingInstagram" class="location-editor__inline-loader" :size="18" />
          </div>
          <div v-if="instagramProfileUrl || instagramStatusMessage" class="location-editor__url-row">
            <a v-if="instagramProfileUrl" class="location-editor__url-link" :href="instagramProfileUrl" target="_blank" rel="noopener noreferrer" @click.stop>
              {{ instagramDisplayUrl }}
              <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
            </a>
            <span
              v-if="instagramStatusMessage"
              class="location-editor__status-row"
              :class="[
                instagramState === 'valid' ? 'location-editor__valid' : '',
                instagramState === 'invalid' ? 'location-editor__instagram-guidance' : '',
                instagramState === 'error' ? 'global-error-string' : '',
              ]"
            >
              {{ instagramStatusMessage }}
            </span>
          </div>
        </div>
      </label>

      <div class="location-editor__field location-editor__field--facebook location-editor__social-row">
        <img class="location-editor__social-logo" src="/images/social-logo-facebook.svg" alt="Facebook" />
        <div class="location-editor__social-body">
          <template v-if="!selectedFacebookName">
            <div class="location-editor__fb-tabs" role="tablist" aria-label="Facebook Page input mode">
              <button
                type="button"
                role="tab"
                :aria-selected="facebookMode === 'search'"
                class="location-editor__fb-tab"
                :class="{ 'location-editor__fb-tab--active': facebookMode === 'search' }"
                @click="setFacebookMode('search')"
              >
                Search
              </button>
              <button
                type="button"
                role="tab"
                :aria-selected="facebookMode === 'manual'"
                class="location-editor__fb-tab"
                :class="{ 'location-editor__fb-tab--active': facebookMode === 'manual' }"
                @click="setFacebookMode('manual')"
              >
                Manual
              </button>
            </div>

            <div v-if="facebookMode === 'search'" class="location-editor__fb-panel">
              <input
                ref="facebookSearchInput"
                v-model="facebookQuery"
                class="location-editor__fb-search-input"
                type="text"
                role="combobox"
                aria-label="Search Facebook Pages"
                aria-autocomplete="list"
                aria-haspopup="listbox"
                :aria-expanded="showFacebookDropdown"
                :aria-controls="showFacebookDropdown ? facebookDropdownId : undefined"
                :aria-activedescendant="showFacebookDropdown && activeFacebookResult >= 0 ? `${facebookDropdownId}-${activeFacebookResult}` : undefined"
                placeholder="Search Facebook Pages by name, URL, @handle, or Page ID"
                maxlength="100"
                autocomplete="off"
                @focus="facebookDropdownOpen = true"
                @input="queueFacebookSearch"
                @keydown="onFacebookSearchKeydown"
              />
              <Teleport to="body">
                <div
                  v-if="showFacebookDropdown"
                  :id="facebookDropdownId"
                  ref="facebookDropdown"
                  class="location-editor__fb-dropdown"
                  role="listbox"
                  aria-label="Facebook Page results"
                  @mousedown.prevent
                >
                  <div v-if="isSearchingFacebook" class="location-editor__fb-status" role="status"><LoadingIcon :size="18" /> Searching...</div>
                  <div v-else-if="facebookErrorMessage" class="location-editor__fb-status global-error-string" role="alert">{{ facebookErrorMessage }}</div>
                  <div v-else-if="hasCompletedFacebookSearch && !facebookResults.length" class="location-editor__fb-status" role="status">
                    No matching Facebook Pages found. Try another name or use Manual with a Page URL or handle.
                  </div>
                  <button
                    v-for="(page, index) in facebookResults"
                    :id="`${facebookDropdownId}-${index}`"
                    :key="page.pageId"
                    type="button"
                    role="option"
                    tabindex="-1"
                    class="location-editor__fb-result"
                    :class="{ 'location-editor__fb-result--active': activeFacebookResult === index }"
                    :aria-label="`Select ${page.name}`"
                    :aria-selected="activeFacebookResult === index"
                    :data-facebook-result-index="index"
                    @click="selectFacebookPage(page)"
                  >
                    <span class="location-editor__fb-result-info">
                      <span class="location-editor__fb-name">{{ page.name }}</span>
                      <span v-if="deriveFacebookVanity(page.url)" class="location-editor__fb-handle">{{ deriveFacebookVanity(page.url) }}</span>
                      <small v-if="page.address">{{ page.address }}</small>
                    </span>
                    <SrpButton tag="span" size="small" color="gray" fill="outlined">Select</SrpButton>
                  </button>
                </div>
              </Teleport>
            </div>

            <template v-else>
              <input
                v-model="facebookManualInput"
                class="global-text-input"
                type="text"
                placeholder="Paste a Facebook URL, handle, or Page ID"
                @input="onFacebookManualInput"
                @keydown.enter.prevent="resolveFacebookManualInput"
                @blur="resolveFacebookManualInput"
              />
              <div v-if="isSearchingFacebook" class="location-editor__fb-status location-editor__fb-status--bare"><LoadingIcon :size="18" /> Resolving...</div>
            </template>
            <p v-if="facebookMode === 'manual' && facebookErrorMessage" class="location-editor__status-row global-error-string location-editor__fb-error">{{ facebookErrorMessage }}</p>
          </template>

          <template v-else>
            <div class="location-editor__fb-selected">
              <span class="location-editor__fb-selected-name">{{ selectedFacebookName }}</span>
              <button type="button" class="location-editor__fb-change" @click="changeFacebookPage">Change</button>
            </div>
            <div v-if="facebookDisplayUrl || isFacebookVerified" class="location-editor__url-row location-editor__url-row--fb">
              <a v-if="facebookDisplayUrl" class="location-editor__url-link" :href="facebookProfileUrl || undefined" target="_blank" rel="noopener noreferrer" @click.stop>
                {{ facebookDisplayUrl }}
                <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
              </a>
              <span v-if="isFacebookVerified" class="location-editor__status-row location-editor__valid">✓ Page found</span>
            </div>
          </template>
          <NoteWithIcon v-if="isFBPending" size="tiny" color="yellow" class="location-editor__fb-pending" style="margin-top: 0">
            <template #text>
              <div>
                <!-- <strong>@{{ unverifiedFBHandle }}</strong><br /> -->
                Tagging will be enabled once an extra verification step is complete.<br />Or use the Search button to find the Page manually.
              </div>
            </template>
          </NoteWithIcon>
        </div>
      </div>

      <label class="location-editor__field location-editor__social-row">
        <img class="location-editor__social-logo" src="/images/social-logo-tiktok.svg" alt="TikTok" />
        <div class="location-editor__social-body">
          <div class="location-editor__handle-input">
            <span>@</span>
            <input v-model="tikTokHandle" type="text" placeholder="username" @input="onTikTokInput" />
          </div>
          <div v-if="tikTokProfileUrl" class="location-editor__url-row">
            <a class="location-editor__url-link" :href="tikTokProfileUrl" target="_blank" rel="noopener noreferrer" @click.stop>
              {{ tikTokDisplayUrl }}
              <IconEmbedded name="external-link_2-5" :size="14" color="#667085" />
            </a>
          </div>
        </div>
      </label>

      <NoteWithIcon v-if="duplicateNameError" size="tiny" color="red" style="margin-top: 20px">
        <template #text>
          {{ duplicateNameError }}
          <button v-if="canUseSaved(duplicateNameLocation)" type="button" class="location-editor__use-saved" @click="useSaved(duplicateNameLocation)">Use the saved one</button>
        </template>
      </NoteWithIcon>
      <NoteWithIcon v-if="duplicatePlaceWarning" size="tiny" color="yellow" :style="{ marginTop: duplicateNameError ? '10px' : '20px' }">
        <template #text>
          {{ duplicatePlaceWarning }}
          <button v-if="canUseSaved(duplicatePlaceLocation)" type="button" class="location-editor__use-saved" @click="useSaved(duplicatePlaceLocation)">Use the saved one</button>
        </template>
      </NoteWithIcon>

      <p v-if="errorMessage" class="global-error-string">{{ errorMessage }}</p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from "vue";
import { autoUpdate, computePosition, flip, offset, shift, size } from "@floating-ui/dom";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import LocationAvatar from "./LocationAvatar.vue";
import LocationCategoryPicker from "./LocationCategoryPicker.vue";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import SocialFieldInputCleaner, { buildFacebookPageUrl, buildInstagramProfileUrl, buildTikTokProfileUrl } from "@logic/SocialFieldInputCleaner";
import { extractAddressParts } from "@logic/GooglePlaceConverter";
import AddressHelper from "@helpers/AddressHelper";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import type { IconName } from "@contracts/IconName";
import { categoryFromGooglePlaceTypes } from "@logic/LocationCategories";
import type { FacebookPageSearchResult, FBVerifyStatus, LocationCategory, LocationData, LocationTagKind, SocialDiscoveryFailMode, WebsiteSocialDiscoveryResult } from "@contracts/socialLocations";

// The fields and logic for adding or editing a Location, with no modal around them. The parent supplies the modal and
// its Save button (see defineExpose at the bottom): LocationEditorModal for the plain editor, AddLocationOrAlbumModal
// for the "New Album" flow.
const props = withDefaults(
  defineProps<{
    customerId: string;
    /** Null to add a new one. */
    location?: LocationData | null;
    /**
     * Adding only. The parent decides the kind (ex. the Location / Event cards in AddLocationOrAlbumModal), so the form
     * doesn't ask "I'm adding". Leave unset to let the user choose the kind here.
     */
    fixedTagKind?: LocationTagKind | null;
    /** Adding only. The name to start with, ex. what was typed before switching over from another kind. */
    initialName?: string;
    /** Shown above the place search, which then also gets the search icon. */
    placeSearchLabel?: string;
    /**
     * Adding only. When the name or place matches a saved Location of one of these kinds, its note offers
     * "Use the saved one" (the useExisting event) on top of blocking the duplicate. Empty means never offer it.
     */
    useExistingKinds?: LocationTagKind[];
  }>(),
  { location: null, fixedTagKind: null, initialName: "", placeSearchLabel: "", useExistingKinds: () => [] }
);
const emit = defineEmits<{
  (e: "saved", location: LocationData): void;
  (e: "useExisting", location: LocationData): void;
}>();

interface SelectedPlaceInfo {
  placeId: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  website: string | null;
  latitude: number | null;
  longitude: number | null;
}

const name = ref("");
const nameInput = ref<HTMLInputElement | null>(null);
const placeSearchInput = ref<{ $el?: HTMLInputElement } | null>(null);
const selectedPlace = ref<SelectedPlaceInfo | null>(null);
const placeChangedThisSession = ref(false);
/** Survives Change (which nulls selectedPlace) so re-picking the same Place does not wipe name/icon/handles. */
const linkedPlaceId = ref<string | null>(null);
const isManualMode = ref(false);
const tagKind = ref<LocationTagKind | null>(null);
/** Only applies to the Location tag kind. Prefilled from Google's place types, always user-editable. */
const category = ref<LocationCategory | null>(null);
const tagKindOptions: { value: LocationTagKind; label: string; icon: IconName }[] = [
  { value: "Location", label: "Business or Location", icon: "map-pin_2" },
  { value: "Person", label: "Person", icon: "user_2" },
  { value: "Event", label: "Event", icon: "calendar_2" }, // removed "or Group" since those are often "businesses" and didn't want confusion
];
const isDiscoveringSocials = ref(false);
const discoveryMessage = ref("");
/** Set when website discovery ran this session; only then do we persist FailMode on save. */
const discoveryFailModeTracked = ref(false);
const failMode = ref<SocialDiscoveryFailMode | null>(null);
const unverifiedFBHandle = ref<string | null>(null);
const fbVerifyStatus = ref<FBVerifyStatus | null>(null);
const existingLocations = ref<LocationData[]>([]);
const duplicateNameError = ref("");
const duplicatePlaceWarning = ref("");
/** The saved Locations the two notes above are about, for "Use the saved one". */
const duplicateNameLocation = ref<LocationData | null>(null);
const duplicatePlaceLocation = ref<LocationData | null>(null);
const isEditingName = ref(false);
const instagramHandle = ref("");
const tikTokHandle = ref("");
const instagramState = ref<"unchecked" | "valid" | "invalid" | "error">("unchecked");
const instagramValidatedAt = ref<string | null>(null);
const originalInstagramHandle = ref("");
const originalInstagramValidatedAt = ref<string | null>(null);
const previewIcon = ref<string | null>(null);
const iconId = ref<string | null>(null);
const isCheckingInstagram = ref(false);
const facebookMode = ref<"search" | "manual">("search");
const facebookQuery = ref("");
const facebookManualInput = ref("");
const facebookResults = ref<FacebookPageSearchResult[]>([]);
const selectedFacebookToken = ref<string | null>(null);
const selectedFacebookPage = ref<FacebookPageSearchResult | null>(null);
const selectedFacebookName = ref("");
const clearExistingFacebook = ref(false);
const isFacebookVerified = ref(false);
const isSearchingFacebook = ref(false);
const hasCompletedFacebookSearch = ref(false);
const facebookSearchInput = ref<HTMLInputElement | null>(null);
const facebookDropdown = ref<HTMLDivElement | null>(null);
const facebookDropdownId = `facebook-pages-${useId()}`;
const facebookDropdownOpen = ref(false);
const activeFacebookResult = ref(-1);
const isSaving = ref(false);
const errorMessage = ref("");
const facebookErrorMessage = ref("");
const lastCheckedInstagramHandle = ref("");
let facebookTimer: ReturnType<typeof setTimeout> | null = null;
let facebookSearchSequence = 0;
let instagramValidationSequence = 0;
let placeDiscoverySequence = 0;
/** Social fields as they were when this Place was picked, so discovery does not clobber edits made while scrape is in flight. */
let discoveryInstagramSnapshot = "";
let discoveryFacebookNameSnapshot = "";
let discoveryFacebookSequence = 0;
let discoveryTikTokSnapshot = "";

/** True while adding with the kind decided by the parent (see the fixedTagKind prop). */
const isKindFixed = computed(() => !props.location && Boolean(props.fixedTagKind));

// Fills the form in. The form is created each time its modal opens (SrpModal only renders its content while it is
// visible), so this runs once per open, and again if the parent swaps the location underneath it.
watch(
  () => props.location?.id,
  () => {
    cancelFacebookLookup();
    const location = props.location;
    name.value = location?.name ?? props.initialName.trim();
    selectedPlace.value = location?.googlePlaceId
      ? {
          placeId: location.googlePlaceId,
          name: location.name,
          address: location.address ?? null,
          city: location.city ?? null,
          state: location.state ?? null,
          country: location.country ?? null,
          website: location.website ?? null,
          latitude: location.latitude ?? null,
          longitude: location.longitude ?? null,
        }
      : null;
    placeChangedThisSession.value = false;
    linkedPlaceId.value = location?.googlePlaceId ?? null;
    placeDiscoverySequence++;
    isDiscoveringSocials.value = false;
    discoveryMessage.value = "";
    discoveryFailModeTracked.value = false;
    failMode.value = location?.failMode ?? null;
    unverifiedFBHandle.value = location?.unverifiedFBHandle ?? null;
    fbVerifyStatus.value = location?.fbVerifyStatus ?? null;
    existingLocations.value = [];
    clearDuplicateChecks();
    originalInstagramHandle.value = normalizeInstagramHandle(location?.socialSet?.instagramHandle ?? "");
    originalInstagramValidatedAt.value = location?.socialSet?.instagramValidatedAt ?? null;
    instagramHandle.value = originalInstagramHandle.value;
    tikTokHandle.value = normalizeTikTokHandle(location?.socialSet?.tikTokHandle ?? "");
    instagramValidatedAt.value = originalInstagramValidatedAt.value;
    instagramState.value = instagramValidatedAt.value ? "valid" : "unchecked";
    lastCheckedInstagramHandle.value = originalInstagramHandle.value;
    previewIcon.value = location?.icon ?? null;
    iconId.value = location?.icon ?? null;
    selectedFacebookToken.value = null;
    selectedFacebookPage.value = null;
    selectedFacebookName.value = location?.socialSet?.facebookPageName ?? "";
    clearExistingFacebook.value = false;
    isFacebookVerified.value = Boolean(location?.socialSet?.facebookPageId);
    facebookMode.value = unverifiedFBHandle.value ? "manual" : "search";
    facebookQuery.value = "";
    facebookManualInput.value = unverifiedFBHandle.value ?? "";
    facebookResults.value = [];
    instagramValidationSequence++;
    isCheckingInstagram.value = false;
    errorMessage.value = "";
    facebookErrorMessage.value = "";
    // A fixed kind other than Location has no place to search for, so it starts on its fields (which is manual entry).
    isManualMode.value = isKindFixed.value && props.fixedTagKind !== "Location";
    tagKind.value = location ? location.tagKind ?? "Location" : props.fixedTagKind;
    category.value = location?.category ?? null;
    isEditingName.value = false;

    void loadExistingLocations();

    if (!location) {
      nextTick(() => {
        // The place search when there is one, otherwise the name.
        (placeSearchInput.value?.$el ?? nameInput.value)?.focus();
      });
    }
  },
  { immediate: true }
);

watch([name, selectedPlace], () => refreshDuplicateChecks());

const hasIdentity = computed(() => Boolean(instagramHandle.value.trim() || selectedFacebookName.value || tikTokHandle.value.trim() || unverifiedFBHandle.value?.trim()));
// Mirrors what the save will actually persist: a place picked now, or the stored one we're leaving alone.
const hasPlaceIdentity = computed(() =>
  Boolean(selectedPlace.value?.placeId || selectedPlace.value?.website || (!placeChangedThisSession.value && (props.location?.googlePlaceId || props.location?.website)))
);
const showPlaceSearch = computed(() => !selectedPlace.value && tagKind.value !== "Person" && tagKind.value !== "Event");
const hasGooglePlaceId = computed(() => Boolean(selectedPlace.value?.placeId || props.location?.googlePlaceId));
const showTagKindPicker = computed(() => !isKindFixed.value && !hasGooglePlaceId.value && (isManualMode.value || Boolean(props.location)));
/** Name and socials stay hidden in manual mode until they pick Person, Location, or Event. */
const showDetailFields = computed(() => Boolean((props.location || selectedPlace.value || isManualMode.value) && tagKind.value));
const namePlaceholder = computed(() => {
  if (tagKind.value === "Person") return "Person's name";
  if (tagKind.value === "Event") return "Event or group name";
  return "Business or place name";
});
const isNameEditMode = computed(() => !name.value.trim() || isEditingName.value);
const isInstagramValidForSave = computed(
  () => !instagramHandle.value.trim() || isOriginalInstagramHandle(instagramHandle.value) || (instagramState.value === "valid" && Boolean(instagramValidatedAt.value))
);
// Same rule as the server (LocationsController.RequiresIdentity). Only a Person has to have an account, because being
// tagged is all a Person is saved for. A Location or Event is also saved to group photos, often before anyone knows
// its accounts, so a name is enough. When every kind needed an account, a festival with none could never be added.
// It applies when editing too, and on every screen: a Location saved with only a name still has to be renamable.
const needsIdentity = computed(() => tagKind.value === "Person");
// showDetailFields: nothing is saved while the name is still off screen. With a name alone now being enough, a name
// carried in through initialName would otherwise enable Save while only the place search is showing.
const canSave = computed(() =>
  Boolean(showDetailFields.value && name.value.trim() && (!needsIdentity.value || hasIdentity.value || hasPlaceIdentity.value) && isInstagramValidForSave.value && !duplicateNameError.value)
);
const isHandleLookupBusy = computed(() => isDiscoveringSocials.value || isCheckingInstagram.value || isSearchingFacebook.value);
const showFacebookDropdown = computed(
  () =>
    facebookDropdownOpen.value &&
    facebookMode.value === "search" &&
    !selectedFacebookName.value &&
    (isSearchingFacebook.value || hasCompletedFacebookSearch.value || Boolean(facebookErrorMessage.value))
);

// Keep the popup outside the modal's scrolling content so it cannot resize or be clipped by it.
watch(
  [showFacebookDropdown, facebookSearchInput, facebookDropdown],
  ([visible, input, dropdown], _, onCleanup) => {
    if (!visible || !input || !dropdown) return;
    const updatePosition = async () => {
      const position = await computePosition(input, dropdown, {
        placement: "bottom-start",
        strategy: "fixed",
        middleware: [
          offset(4),
          flip({ padding: 8 }),
          shift({ padding: 8 }),
          size({
            padding: 8,
            apply({ rects, availableHeight, availableWidth, elements }) {
              Object.assign(elements.floating.style, {
                width: `${Math.min(rects.reference.width, availableWidth)}px`,
                maxHeight: `${Math.max(0, Math.min(320, availableHeight))}px`,
              });
            },
          }),
        ],
      });
      if (facebookDropdown.value !== dropdown || !showFacebookDropdown.value) return;
      Object.assign(dropdown.style, { left: `${position.x}px`, top: `${position.y}px` });
    };
    onCleanup(autoUpdate(input, dropdown, updatePosition));
  },
  { flush: "post" }
);

const isFBPending = computed(() => fbVerifyStatus.value === "Pending" && Boolean(unverifiedFBHandle.value));
// Only link out to a handle we've confirmed. While someone is retyping it, the link would point
// at an account that may not exist, so it stays hidden until the new handle checks out.
const instagramProfileUrl = computed(() =>
  instagramState.value === "valid" || isOriginalInstagramHandle(instagramHandle.value) ? buildInstagramProfileUrl(normalizeInstagramHandle(instagramHandle.value)) : ""
);
const instagramDisplayUrl = computed(() => stripUrlProtocol(instagramProfileUrl.value));
// TikTok has no validation endpoint, so show a preview link whenever a handle is present.
const tikTokProfileUrl = computed(() => {
  const handle = normalizeTikTokHandle(tikTokHandle.value);
  return handle ? buildTikTokProfileUrl(handle) : "";
});
const tikTokDisplayUrl = computed(() => stripUrlProtocol(tikTokProfileUrl.value));
const facebookProfileUrl = computed(() => {
  if (selectedFacebookPage.value) {
    return buildFacebookPageUrl(selectedFacebookPage.value.url, selectedFacebookPage.value.pageId);
  }
  if (clearExistingFacebook.value || !selectedFacebookName.value) return "";
  const socialSet = props.location?.socialSet;
  return buildFacebookPageUrl(socialSet?.facebookPageUrl, socialSet?.facebookPageId);
});
const facebookDisplayUrl = computed(() => {
  const vanity = deriveFacebookVanity(facebookProfileUrl.value);
  return vanity ? `facebook.com/${vanity}` : stripUrlProtocol(facebookProfileUrl.value).replace(/^www\./i, "");
});
const instagramStatusMessage = computed(() => {
  if (instagramState.value === "valid") return "✓ Account found";
  if (instagramState.value === "invalid") return "Couldn't find that Instagram account. Check the handle and try again.";
  if (instagramState.value === "error") return "Couldn't verify that Instagram account right now. Please try again.";
  return "";
});

function stripUrlProtocol(url: string): string {
  return url.replace(/^https?:\/\//i, "");
}

function mapPlaceToInfo(place: globalThis.google.maps.places.PlaceResult): SelectedPlaceInfo {
  const addressParts = extractAddressParts(place);
  return {
    placeId: place.place_id ?? "",
    name: place.name ?? "",
    address: place.formatted_address ? AddressHelper.removeCountryAndZipCode(place.formatted_address) : null,
    city: addressParts.city ?? null,
    state: addressParts.state ?? null,
    country: addressParts.country ?? null,
    website: place.website ?? null,
    latitude: place.geometry?.location?.lat?.() ?? null,
    longitude: place.geometry?.location?.lng?.() ?? null,
  };
}

async function onGooglePlaceSelected(place: globalThis.google.maps.places.PlaceResult) {
  // Fires with no place_id when someone types free text without picking a suggestion.
  if (!place?.place_id) return;
  getGlobalRemoteLogger().info(`LocationEditor googlePlaceSelected: ${place.name} ${place.place_id}`);

  const placeInfo = mapPlaceToInfo(place);
  const isNewPlace = placeInfo.placeId !== (selectedPlace.value?.placeId || linkedPlaceId.value);
  selectedPlace.value = placeInfo;
  linkedPlaceId.value = placeInfo.placeId;
  placeChangedThisSession.value = true;
  isManualMode.value = false;
  tagKind.value = "Location";

  if (isNewPlace) {
    name.value = placeInfo.name;
    isEditingName.value = false;
    // Keep a category the user already chose when Google gives us nothing to go on.
    category.value = categoryFromGooglePlaceTypes(place.types) ?? category.value;
    clearIcon();
    instagramValidationSequence++;
    cancelFacebookLookup();
    discoveryFailModeTracked.value = true;
    failMode.value = null;
    unverifiedFBHandle.value = null;
    fbVerifyStatus.value = null;
    discoveryInstagramSnapshot = normalizeInstagramHandle(instagramHandle.value);
    discoveryFacebookNameSnapshot = selectedFacebookName.value;
    discoveryFacebookSequence = facebookSearchSequence;
    discoveryTikTokSnapshot = normalizeTikTokHandle(tikTokHandle.value);
  }

  const sequence = ++placeDiscoverySequence;
  discoveryMessage.value = "";

  if (!placeInfo.website) {
    discoveryMessage.value = "No website found for this location - add social accounts below.";
    return;
  }
  if (!isNewPlace) return;
  await runSocialDiscovery(sequence, placeInfo.website);
}

function onAutocompleteError(error: any) {
  getGlobalRemoteLogger().error(`LocationEditor Autocomplete Error in Google Maps: ${error}`);
}

function changePlace() {
  selectedPlace.value = null;
  // Handles/name/icon are intentionally kept, so keep the fields on screen rather than hiding
  // what they already entered behind the search box.
  isManualMode.value = true;
  // Cancels applying any in-flight discovery.
  placeDiscoverySequence++;
  isDiscoveringSocials.value = false;
  discoveryMessage.value = "";
  // Those modes describe the website just dropped. Facebook fail modes stay, because that handle may still be kept.
  if (failMode.value === "SiteUnavailable" || failMode.value === "WebsiteIsSpa") failMode.value = null;
}

async function loadExistingLocations() {
  try {
    existingLocations.value = await SocialLocationsRepo.list(props.customerId);
    refreshDuplicateChecks();
  } catch {
    // Non-blocking: duplicate checks are a nicety; don't lock the user out if the list fails.
    existingLocations.value = [];
    clearDuplicateChecks();
  }
}

function clearDuplicateChecks() {
  duplicateNameError.value = "";
  duplicatePlaceWarning.value = "";
  duplicateNameLocation.value = null;
  duplicatePlaceLocation.value = null;
}

function refreshDuplicateChecks() {
  const editingId = props.location?.id;
  const activeLocations = existingLocations.value.filter(l => !l.deletedDate && l.id !== editingId);
  const trimmedName = name.value.trim();
  const placeId = selectedPlace.value?.placeId;

  const nameDuplicate = trimmedName ? activeLocations.find(l => l.name.trim().toLowerCase() === trimmedName.toLowerCase()) : undefined;
  const placeDuplicate = placeId ? activeLocations.find(l => l.googlePlaceId === placeId) : undefined;

  duplicateNameError.value = nameDuplicate ? `You already have this location saved: "${nameDuplicate.name}"` : "";
  duplicateNameLocation.value = nameDuplicate ?? null;
  // Same location matching both: only the red name error. Different locations: show both.
  const showPlaceWarning = Boolean(placeDuplicate && (!nameDuplicate || placeDuplicate.id !== nameDuplicate.id));
  duplicatePlaceWarning.value = showPlaceWarning ? `You already have a Location for this Google Place: "${placeDuplicate!.name}"` : "";
  duplicatePlaceLocation.value = (showPlaceWarning && placeDuplicate) || null;
}

/** Whether a duplicate note should offer "Use the saved one" for the Location it is about (see the useExistingKinds prop). */
function canUseSaved(location: LocationData | null): boolean {
  // Missing/legacy tagKind is treated as a Location, as elsewhere.
  return Boolean(location) && !props.location && props.useExistingKinds.includes(location.tagKind ?? "Location");
}

function useSaved(location: LocationData | null) {
  if (location) emit("useExisting", location);
}

async function runSocialDiscovery(sequence: number, websiteUrl: string) {
  isDiscoveringSocials.value = true;
  try {
    const result = await SocialLocationsRepo.discoverSocialsFromWebsite(props.customerId, websiteUrl);
    if (sequence !== placeDiscoverySequence) return;
    await applyDiscoveryResults(sequence, result);
  } catch {
    if (sequence !== placeDiscoverySequence) return;
    discoveryMessage.value = "Couldn't find social accounts from the website - add them below.";
  } finally {
    if (sequence === placeDiscoverySequence) isDiscoveringSocials.value = false;
  }
}

/** Replaces a handle only after Instagram/Facebook validation succeeds (TikTok has no validator). Otherwise the existing value stays. */
async function applyDiscoveryResults(sequence: number, result: WebsiteSocialDiscoveryResult) {
  const tasks: Promise<void>[] = [];
  const found: string[] = [];

  discoveryFailModeTracked.value = true;
  failMode.value = result.siteUnavailable ? "SiteUnavailable" : result.websiteIsSpa ? "WebsiteIsSpa" : null;

  const instagramCandidate = topCandidate(result.instagram);
  if (instagramCandidate) {
    const handle = normalizeInstagramHandle(instagramCandidate.handle || instagramCandidate.url);
    if (handle && handle !== discoveryInstagramSnapshot && normalizeInstagramHandle(instagramHandle.value) === discoveryInstagramSnapshot) {
      const igSequence = instagramValidationSequence;
      tasks.push(
        (async () => {
          if (await applyDiscoveredInstagram(handle, igSequence)) found.push("Instagram");
        })()
      );
    }
  }

  const facebookCandidate = topCandidate(result.facebook);
  if (facebookCandidate) {
    const input = facebookCandidate.url || facebookCandidate.handle;
    if (input && selectedFacebookName.value === discoveryFacebookNameSnapshot && facebookSearchSequence === discoveryFacebookSequence) {
      unverifiedFBHandle.value = facebookCandidateHandle(facebookCandidate);
      tasks.push(
        (async () => {
          if (await autoResolveFacebook(input)) found.push("Facebook");
        })()
      );
    }
  }

  const tikTokCandidate = topCandidate(result.tiktok);
  if (tikTokCandidate) {
    const handle = normalizeTikTokHandle(tikTokCandidate.handle || tikTokCandidate.url);
    if (handle && handle !== discoveryTikTokSnapshot && normalizeTikTokHandle(tikTokHandle.value) === discoveryTikTokSnapshot) {
      tikTokHandle.value = handle;
      found.push("TikTok");
    }
  }

  await Promise.allSettled(tasks);
  if (sequence !== placeDiscoverySequence) return;
  if (found.length) {
    discoveryMessage.value = `Found ${formatFoundList(found)} - review below.`;
  } else if (failMode.value === "CantVerifyFacebook" && unverifiedFBHandle.value) {
    discoveryMessage.value = "Found a Facebook Page that needs verification - review below.";
  } else {
    discoveryMessage.value = "Couldn't find social accounts - please add below.";
  }

  // Website apple-touch-icon only when IG/FB did not already set an icon (never overwrite).
  const iconUrls = (result.websiteIconUrls?.length ? result.websiteIconUrls : result.websiteIconUrl ? [result.websiteIconUrl] : []).filter(Boolean);
  if (!iconId.value && iconUrls.length) {
    for (const iconUrl of iconUrls) {
      if (iconId.value) break;
      try {
        const importedIconId = await SocialLocationsRepo.importWebsiteIcon(props.customerId, iconUrl);
        if (sequence !== placeDiscoverySequence) return;
        if (importedIconId && !iconId.value) {
          iconId.value = importedIconId;
          previewIcon.value = importedIconId;
          break;
        }
      } catch {
        // Non-blocking: try the next candidate; initials remain if none import.
      }
    }
  }
}

async function autoResolveFacebook(input: string): Promise<boolean> {
  cancelFacebookLookup();
  const sequence = facebookSearchSequence;
  const hadExisting = Boolean(selectedFacebookName.value);
  try {
    const resolved = await SocialLocationsRepo.resolveFacebookPage(props.customerId, normalizeFacebookResolveInput(input), true);
    // Bail if the customer started their own lookup while this was running.
    if (sequence !== facebookSearchSequence) return false;
    if (selectedFacebookName.value !== discoveryFacebookNameSnapshot) return false;
    selectFacebookPage(resolved);
    if (discoveryFailModeTracked.value && failMode.value === "CantVerifyFacebook") failMode.value = null;
    return true;
  } catch (error: any) {
    if (sequence !== facebookSearchSequence) return false;
    // Keep an existing Page. Only record pending verification when there was nothing to keep.
    if (hadExisting) {
      unverifiedFBHandle.value = null;
      fbVerifyStatus.value = null;
    } else if (discoveryFailModeTracked.value) {
      markFacebookResolutionPending(input, facebookFailModeFromError(error));
    }
    return false;
  }
}

/** Validates a discovered handle without touching the current input first, then replaces it only when the account exists. */
async function applyDiscoveredInstagram(handle: string, sequence: number): Promise<boolean> {
  isCheckingInstagram.value = true;
  try {
    const result = await SocialLocationsRepo.validateInstagram(props.customerId, handle, true);
    if (sequence !== instagramValidationSequence) return false;
    if (normalizeInstagramHandle(instagramHandle.value) !== discoveryInstagramSnapshot) return false;
    if (!result.isValid) return false;
    instagramHandle.value = result.username;
    lastCheckedInstagramHandle.value = normalizeInstagramHandle(result.username);
    instagramValidatedAt.value = result.validatedAt ?? new Date().toISOString();
    instagramState.value = "valid";
    if (result.name && !name.value.trim()) name.value = result.name;
    if (result.iconId) {
      iconId.value = result.iconId;
      previewIcon.value = result.iconId;
    }
    return true;
  } catch {
    return false;
  } finally {
    if (sequence === instagramValidationSequence) isCheckingInstagram.value = false;
  }
}

function topCandidate(candidates: WebsiteSocialDiscoveryResult["instagram"]) {
  return candidates?.slice().sort((a, b) => a.rank - b.rank)[0] ?? null;
}

function facebookCandidateHandle(candidate: NonNullable<ReturnType<typeof topCandidate>>): string | null {
  const handle = (candidate.handle || deriveFacebookVanity(candidate.url) || candidate.url || "").trim().replace(/^@+/, "");
  return handle || null;
}

function formatFoundList(found: string[]): string {
  if (found.length === 1) return found[0];
  return `${found.slice(0, -1).join(", ")} and ${found[found.length - 1]}`;
}

function isOriginalInstagramHandle(value: string): boolean {
  return Boolean(props.location && originalInstagramHandle.value && normalizeInstagramHandle(value) === originalInstagramHandle.value);
}

function restoreOriginalInstagramValidation() {
  instagramState.value = originalInstagramValidatedAt.value ? "valid" : "unchecked";
  instagramValidatedAt.value = originalInstagramValidatedAt.value;
  lastCheckedInstagramHandle.value = originalInstagramHandle.value;
}

function onInstagramInput() {
  instagramValidationSequence++;
  isCheckingInstagram.value = false;
  instagramHandle.value = normalizeInstagramHandle(instagramHandle.value);
  if (isOriginalInstagramHandle(instagramHandle.value)) {
    restoreOriginalInstagramValidation();
    return;
  }
  instagramState.value = "unchecked";
  instagramValidatedAt.value = null;
  lastCheckedInstagramHandle.value = "";
}

function onTikTokInput() {
  tikTokHandle.value = normalizeTikTokHandle(tikTokHandle.value);
}

async function handleInstagramBlur() {
  const normalizedHandle = normalizeInstagramHandle(instagramHandle.value);
  if (!normalizedHandle) {
    instagramHandle.value = "";
    instagramState.value = "unchecked";
    instagramValidatedAt.value = null;
    lastCheckedInstagramHandle.value = "";
    return;
  }
  instagramHandle.value = normalizedHandle;
  if (isOriginalInstagramHandle(normalizedHandle)) {
    restoreOriginalInstagramValidation();
    return;
  }
  if (normalizedHandle === lastCheckedInstagramHandle.value) return;
  const sequence = ++instagramValidationSequence;
  await checkInstagram(normalizedHandle, sequence);
}

async function beginNameEdit() {
  isEditingName.value = true;
  await nextTick();
  nameInput.value?.focus();
  nameInput.value?.select();
}

function finishNameEdit() {
  isEditingName.value = false;
}

function clearIcon() {
  previewIcon.value = null;
  iconId.value = null;
}

async function checkInstagram(handle: string, sequence: number) {
  isCheckingInstagram.value = true;
  errorMessage.value = "";
  try {
    const result = await SocialLocationsRepo.validateInstagram(props.customerId, handle, true);
    if (sequence !== instagramValidationSequence || normalizeInstagramHandle(instagramHandle.value) !== handle) return;
    instagramState.value = result.isValid ? "valid" : "invalid";
    lastCheckedInstagramHandle.value = handle;
    if (result.isValid) {
      instagramHandle.value = result.username;
      lastCheckedInstagramHandle.value = normalizeInstagramHandle(result.username);
      instagramValidatedAt.value = result.validatedAt ?? new Date().toISOString();
      // Don't overwrite a name/icon we already have (ex. from the Google Place the customer picked).
      if (result.name && !name.value.trim()) name.value = result.name;
      if (result.iconId) {
        iconId.value = result.iconId;
        previewIcon.value = result.iconId;
      }
    } else {
      instagramValidatedAt.value = null;
    }
  } catch {
    if (sequence !== instagramValidationSequence || normalizeInstagramHandle(instagramHandle.value) !== handle) return;
    instagramState.value = "error";
    instagramValidatedAt.value = null;
  } finally {
    if (sequence === instagramValidationSequence) isCheckingInstagram.value = false;
  }
}

function normalizeInstagramHandle(value: string): string {
  return SocialFieldInputCleaner.cleanInstagramHandle(value).trim().replace(/^@+/, "").toLowerCase();
}

function normalizeTikTokHandle(value: string): string {
  return SocialFieldInputCleaner.cleanTikTokHandle(value).trim();
}

function cancelFacebookLookup() {
  if (facebookTimer) clearTimeout(facebookTimer);
  facebookTimer = null;
  facebookSearchSequence++;
  isSearchingFacebook.value = false;
  hasCompletedFacebookSearch.value = false;
  facebookResults.value = [];
  facebookDropdownOpen.value = false;
  activeFacebookResult.value = -1;
}

function dismissFacebookDropdown(event: Event) {
  const target = event.target as Node | null;
  if (target && !facebookSearchInput.value?.contains(target) && !facebookDropdown.value?.contains(target)) {
    facebookDropdownOpen.value = false;
    activeFacebookResult.value = -1;
  }
}

function onFacebookSearchKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    if (showFacebookDropdown.value) {
      event.preventDefault();
      event.stopPropagation();
    }
    facebookDropdownOpen.value = false;
    activeFacebookResult.value = -1;
  } else if (event.key === "Tab") {
    facebookDropdownOpen.value = false;
  } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    facebookDropdownOpen.value = true;
    if (!facebookResults.value.length) return;
    const count = facebookResults.value.length;
    activeFacebookResult.value = event.key === "ArrowDown" ? (activeFacebookResult.value + 1) % count : activeFacebookResult.value < 0 ? count - 1 : (activeFacebookResult.value - 1 + count) % count;
    nextTick(() => facebookDropdown.value?.querySelector<HTMLElement>(`[data-facebook-result-index="${activeFacebookResult.value}"]`)?.scrollIntoView({ block: "nearest" }));
  } else if (event.key === "Enter" && showFacebookDropdown.value && activeFacebookResult.value >= 0) {
    event.preventDefault();
    selectFacebookPage(facebookResults.value[activeFacebookResult.value]);
  }
}

onMounted(() => {
  document.addEventListener("pointerdown", dismissFacebookDropdown, true);
  document.addEventListener("focusin", dismissFacebookDropdown);
});

onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", dismissFacebookDropdown, true);
  document.removeEventListener("focusin", dismissFacebookDropdown);
  cancelFacebookLookup();
  placeDiscoverySequence++;
  instagramValidationSequence++;
});

function setFacebookMode(mode: "search" | "manual") {
  if (facebookMode.value === mode) return;
  facebookMode.value = mode;
  cancelFacebookLookup();
  facebookErrorMessage.value = "";
}

function queueFacebookSearch() {
  cancelFacebookLookup();
  const sequence = facebookSearchSequence;
  facebookErrorMessage.value = "";
  const input = facebookQuery.value.trim();
  if (input.length < 2) return;
  facebookDropdownOpen.value = true;
  if (input.length > 100) {
    facebookErrorMessage.value = "Enter no more than 100 characters to search Facebook Pages.";
    return;
  }
  facebookTimer = setTimeout(() => runFacebookLookup(sequence, input, "search"), 350);
}

async function resolveFacebookManualInput() {
  if (facebookTimer) clearTimeout(facebookTimer);
  const input = normalizeFacebookResolveInput(facebookManualInput.value.trim());
  if (!input) {
    clearUnverifiedFacebook();
    return;
  }

  const inputKind = classifyFacebookInput(input);
  if (inputKind !== "resolve") {
    facebookErrorMessage.value = facebookResolveErrorMessage;
    return;
  }

  const sequence = ++facebookSearchSequence;
  facebookResults.value = [];
  await runFacebookLookup(sequence, input, "resolve");
}

function onFacebookManualInput() {
  cancelFacebookLookup();
  facebookErrorMessage.value = "";
  if (!facebookManualInput.value.trim()) clearUnverifiedFacebook();
}

function clearUnverifiedFacebook() {
  unverifiedFBHandle.value = null;
  fbVerifyStatus.value = null;
  facebookErrorMessage.value = "";
  if (failMode.value === "CantVerifyFacebook" || failMode.value === "AgeGated") {
    discoveryFailModeTracked.value = true;
    failMode.value = null;
  }
}

async function runFacebookLookup(sequence: number, input: string, inputKind: "search" | "resolve") {
  if (sequence !== facebookSearchSequence) return;
  isSearchingFacebook.value = true;
  facebookErrorMessage.value = "";
  try {
    if (inputKind === "resolve") {
      const result = await SocialLocationsRepo.resolveFacebookPage(props.customerId, input, true);
      if (sequence === facebookSearchSequence) {
        selectFacebookPage(result);
        isFacebookVerified.value = true;
      }
    } else {
      const results = await SocialLocationsRepo.searchFacebookPages(props.customerId, input);
      if (sequence === facebookSearchSequence) {
        facebookResults.value = results;
        hasCompletedFacebookSearch.value = true;
      }
    }
  } catch (error: any) {
    if (sequence === facebookSearchSequence) {
      if (inputKind === "resolve") {
        markFacebookResolutionPending(input, facebookFailModeFromError(error));
        facebookErrorMessage.value = unverifiedFBHandle.value ? "" : facebookResolveErrorMessage;
      } else {
        facebookErrorMessage.value = getErrorMessage(error);
      }
    }
  } finally {
    if (sequence === facebookSearchSequence) isSearchingFacebook.value = false;
  }
}

const facebookResolveErrorMessage = "Couldn't resolve that Facebook Page. Try its handle, url, Page Id, or search by name.";

function classifyFacebookInput(input: string): "search" | "resolve" | "partial-url" | "invalid-url" {
  if (/^[0-9]{5,25}$/.test(input)) return "resolve";
  if (/^[0-9]+$/.test(input)) return input.length < 5 ? "partial-url" : "invalid-url";
  if (isSupportedFacebookUrl(input)) return "resolve";
  if (isFacebookHandle(input)) return "resolve";

  const looksLikeFacebookUrl = /^(?:https?:\/\/)?(?:(?:www|m|web)\.)?(?:facebook\.com|fb\.com)(?:[/?#]|$)/i.test(input);
  if (looksLikeFacebookUrl) return "partial-url";
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(input)) return "invalid-url";
  return "search";
}

const facebookAllowedHosts = new Set(["facebook.com", "www.facebook.com", "m.facebook.com", "web.facebook.com", "fb.com", "www.fb.com"]);
const facebookReservedPaths = new Set([
  "about",
  "ads",
  "business",
  "events",
  "groups",
  "help",
  "home.php",
  "login",
  "marketplace",
  "messages",
  "people",
  "photo",
  "photo.php",
  "photos",
  "plugins",
  "privacy",
  "profile.php",
  "reel",
  "reels",
  "share",
  "stories",
  "story.php",
  "watch",
  "permalink.php",
  "pages",
  "pg",
  "p",
]);

function isFacebookHandle(input: string): boolean {
  const handle = input.replace(/^@+/, "").trim();
  if (!/^[A-Za-z0-9.]{2,75}$/.test(handle) || /^[0-9]+$/.test(handle)) return false;
  return !facebookReservedPaths.has(handle.toLowerCase()) && !facebookAllowedHosts.has(handle.toLowerCase());
}

function isSupportedFacebookUrl(input: string): boolean {
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(input) ? input : `https://${input}`;
  try {
    const url = new URL(candidate);
    return (url.protocol === "https:" || url.protocol === "http:") && facebookAllowedHosts.has(url.hostname.toLowerCase()) && url.pathname !== "/";
  } catch {
    return false;
  }
}

function isFacebookPageIdentitySegment(segment: string): boolean {
  if (facebookReservedPaths.has(segment.toLowerCase())) return false;
  return /^[0-9]{5,25}$/.test(segment) || /^[A-Za-z0-9.]{2,75}$/.test(segment);
}

/** Normalize pasteable Facebook URLs into a handle, Page ID, or cleaned vanity URL. */
function normalizeFacebookResolveInput(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(candidate);
    if (!facebookAllowedHosts.has(url.hostname.toLowerCase())) return trimmed;
    const segments = url.pathname
      .split("/")
      .filter(Boolean)
      .map(s => decodeURIComponent(s));
    if (segments.length === 0) return trimmed;

    if (/^profile\.php$/i.test(segments[0])) {
      const id = url.searchParams.get("id")?.trim() ?? "";
      if (/^[0-9]{5,25}$/.test(id)) return id;
      return trimmed;
    }
    if (segments.length >= 3 && /^people$/i.test(segments[0]) && /^[0-9]{5,25}$/.test(segments[2])) return segments[2];
    if (segments.length >= 2 && /^p$/i.test(segments[0])) {
      const slug = segments[1];
      const dash = slug.lastIndexOf("-");
      if (dash >= 0) {
        const id = slug.slice(dash + 1);
        if (/^[0-9]{5,25}$/.test(id)) return id;
      }
    }
    if (segments.length >= 2 && /^pages$/i.test(segments[0]) && /^[0-9]{5,25}$/.test(segments[segments.length - 1])) {
      return segments[segments.length - 1];
    }
    if (segments.length >= 2 && /^pg$/i.test(segments[0]) && isFacebookPageIdentitySegment(segments[1])) return segments[1];
    if (isFacebookPageIdentitySegment(segments[0])) return segments[0];
  } catch {
    return trimmed;
  }
  return trimmed;
}

function deriveFacebookVanity(url?: string | null): string {
  const input = (url || "").trim();
  if (!input) return "";
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(input) ? input : `https://${input}`;
  try {
    const parsed = new URL(candidate);
    if (!/(^|\.)(facebook|fb)\.com$/i.test(parsed.hostname)) return "";
    const segments = parsed.pathname.split("/").filter(Boolean);
    if (segments.length === 0) return "";
    const segment = decodeURIComponent(segments[0]);
    if (/^[0-9]+$/.test(segment) || facebookReservedPaths.has(segment.toLowerCase())) return "";
    if (!/^[A-Za-z0-9.]{2,75}$/.test(segment)) return "";
    return segment;
  } catch {
    return "";
  }
}

function markFacebookResolutionPending(input: string, pendingFailMode: SocialDiscoveryFailMode = "CantVerifyFacebook") {
  const normalizedInput = normalizeFacebookResolveInput(input).replace(/^@+/, "").trim();
  // Vanity handles, numeric Page IDs (people/profile.php//p/), or a handle already stashed from website discovery.
  let handle = "";
  if (isFacebookHandle(normalizedInput)) handle = normalizedInput;
  else if (/^[0-9]{5,25}$/.test(normalizedInput)) handle = normalizedInput;
  else handle = deriveFacebookVanity(input);
  if (!handle) handle = (unverifiedFBHandle.value || "").trim();
  if (!handle) return;
  discoveryFailModeTracked.value = true;
  failMode.value = pendingFailMode;
  unverifiedFBHandle.value = handle;
  fbVerifyStatus.value = "Pending";
  facebookMode.value = "manual";
  facebookManualInput.value = handle;
}

function facebookFailModeFromError(error: any): SocialDiscoveryFailMode {
  const data = error?.response?.data;
  const reason = data && typeof data === "object" ? data.reason ?? data.Reason : undefined;
  return reason === "LoginWall" ? "AgeGated" : "CantVerifyFacebook";
}

function selectFacebookPage(page: FacebookPageSearchResult) {
  cancelFacebookLookup();
  selectedFacebookToken.value = page.selectionToken ?? null;
  selectedFacebookPage.value = page;
  selectedFacebookName.value = page.name;
  unverifiedFBHandle.value = null;
  fbVerifyStatus.value = "Verified";
  if (discoveryFailModeTracked.value && (failMode.value === "CantVerifyFacebook" || failMode.value === "AgeGated")) failMode.value = null;
  clearExistingFacebook.value = false;
  isFacebookVerified.value = Boolean(page.pageId);
  if (!name.value.trim() && page.name) name.value = page.name;
  if (!iconId.value && page.iconId) {
    iconId.value = page.iconId;
    previewIcon.value = page.iconId;
  }
  facebookResults.value = [];
  facebookQuery.value = "";
  facebookManualInput.value = "";
}

function clearFacebookPage() {
  cancelFacebookLookup();
  selectedFacebookToken.value = null;
  selectedFacebookPage.value = null;
  selectedFacebookName.value = "";
  isFacebookVerified.value = false;
  unverifiedFBHandle.value = null;
  fbVerifyStatus.value = null;
  clearExistingFacebook.value = Boolean(props.location?.socialSet?.facebookPageId);
}

function changeFacebookPage() {
  clearFacebookPage();
  facebookMode.value = "search";
  facebookQuery.value = "";
  facebookManualInput.value = "";
  facebookResults.value = [];
  facebookErrorMessage.value = "";
}

async function save() {
  if (isSaving.value || isHandleLookupBusy.value || !canSave.value) return;
  isSaving.value = true;
  errorMessage.value = "";
  try {
    const request = {
      eTag: props.location?._etag,
      name: name.value.trim(),
      instagramHandle: normalizeInstagramHandle(instagramHandle.value) || null,
      instagramValidatedAt: instagramValidatedAt.value,
      iconId: iconId.value,
      facebookPageSelectionToken: selectedFacebookToken.value,
      facebookPageSelection: selectedFacebookPage.value
        ? {
            pageId: selectedFacebookPage.value.pageId,
            pageName: selectedFacebookPage.value.name,
            pageUrl: selectedFacebookPage.value.url ?? null,
            address: selectedFacebookPage.value.address ?? null,
            latitude: selectedFacebookPage.value.latitude ?? null,
            longitude: selectedFacebookPage.value.longitude ?? null,
            validatedAt: selectedFacebookPage.value.validatedAt ?? null,
            loadType: selectedFacebookPage.value.loadType ?? null,
          }
        : null,
      clearFacebookPage: clearExistingFacebook.value,
      tikTokHandle: normalizeTikTokHandle(tikTokHandle.value) || null,
      // Only sent when a place was picked this session. Otherwise the server keeps what it already has.
      googlePlaceId: placeChangedThisSession.value ? selectedPlace.value?.placeId ?? null : null,
      website: placeChangedThisSession.value ? selectedPlace.value?.website ?? null : null,
      address: placeChangedThisSession.value ? selectedPlace.value?.address ?? null : null,
      city: placeChangedThisSession.value ? selectedPlace.value?.city ?? null : null,
      state: placeChangedThisSession.value ? selectedPlace.value?.state ?? null : null,
      country: placeChangedThisSession.value ? selectedPlace.value?.country ?? null : null,
      latitude: placeChangedThisSession.value ? selectedPlace.value?.latitude ?? null : null,
      longitude: placeChangedThisSession.value ? selectedPlace.value?.longitude ?? null : null,
      updateFailMode: discoveryFailModeTracked.value,
      failMode: discoveryFailModeTracked.value ? failMode.value : undefined,
      isManualMode: isManualMode.value,
      tagKind: tagKind.value ?? "Location",
      category: (tagKind.value ?? "Location") === "Location" ? category.value : null,
      unverifiedFBHandle: unverifiedFBHandle.value || null,
    };
    const saved = props.location ? await SocialLocationsRepo.update(props.customerId, props.location.id, request) : await SocialLocationsRepo.create(props.customerId, request);
    emit("saved", saved);
  } catch (error: any) {
    errorMessage.value = getErrorMessage(error);
  } finally {
    isSaving.value = false;
  }
}

function getErrorMessage(error: any): string {
  const value = error?.response?.data;
  if (typeof value === "string") return value;
  return value?.detail ?? value?.title ?? error?.message ?? "Unable to save this Location.";
}

/** What the user has typed as a name so far: the name field, or the text in the place search when no place is picked yet. */
function getDraftName(): string {
  return name.value.trim() || placeSearchInput.value?.$el?.value?.trim() || "";
}

// The Save button sits in the parent's modal footer, outside this form, so the parent drives the save from here.
defineExpose({ save, canSave, isSaving, isBusy: isHandleLookupBusy, getDraftName });
</script>

<style scoped lang="scss">
.location-editor {
  display: grid;
  gap: 12px;
}
.location-editor__identity {
  display: flex;
  align-items: center;
  gap: 12px;
}
.location-editor__search {
  display: grid;
  gap: 7px;
}
// A link inside a duplicate note, so it takes the note's own color (red or yellow).
.location-editor__use-saved {
  margin-left: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}
.location-editor__use-saved:hover {
  text-decoration: none;
}
.location-editor__use-saved:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
  border-radius: 3px;
}
.location-editor__avatar-wrap {
  position: relative;
  flex: 0 0 auto;
  // padding: 4px 4px 0 0;
}
.location-editor__clear-icon {
  position: absolute;
  top: 0;
  right: 0;
  display: inline-grid;
  place-items: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 1px solid #98a2b3;
  border-radius: 999px;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
}
.location-editor__clear-icon:hover {
  color: #101828;
  border-color: #667085;
  background: #f9fafb;
}
.location-editor__name-row {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.location-editor__name-display {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: #000;
  font-size: 18px;
  font-weight: 600;
}
.location-editor__name-display span {
  // overflow: hidden;
  // text-overflow: ellipsis;
  // white-space: nowrap;
}
.location-editor__name-display button {
  display: inline-grid;
  place-items: center;
  flex: 0 0 auto;
  padding: 3px;
  border: 0;
  background: transparent;
  color: #667085;
  cursor: pointer;
}
.location-editor__field {
  display: grid;
  gap: 7px;
  font-size: 14px;
  font-weight: 600;
}
.location-editor__kind {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 12px;
}
.location-editor__kind-label {
  flex: 0 0 auto;
  color: #101828;
  font-size: 14px;
  // font-weight: 600;
}
.location-editor__required {
  color: #ec573b;
}
.location-editor__kind-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.location-editor__kind-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  color: #666;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}
.location-editor__kind-option:hover:not(.location-editor__kind-option--selected) {
  border-color: #bbb;
}
.location-editor__kind-option:focus-visible {
  outline: 2px solid rgba(17, 134, 137, 0.5);
  outline-offset: 1px;
}
.location-editor__kind-option--selected {
  background: rgba(17, 134, 137, 0.06);
  border-color: #118689;
  color: #0d686b;
}
.location-editor__field--grow {
  flex: 1;
}
// The Facebook panel pieces manage their own spacing (the tabs sit flush against the panel).
.location-editor__field--facebook .location-editor__social-body {
  gap: 0;
}
.location-editor__fb-pending {
  margin-top: 8px;
}
.location-editor__field--facebook .location-editor__social-logo {
  margin-top: 14px;
}
.location-editor__identity-body {
  display: grid;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.location-editor__place-address {
  color: #667085;
  font-size: 13px;
  font-weight: 400;
}
.location-editor__identity-body .location-editor__url-link {
  justify-self: start;
  font-size: 14px;
}
.location-editor__social-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
// Profile url and its status sit on one line, wrapping when the status message is long.
.location-editor__url-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 12px;
  min-width: 0;
}
// The Facebook panel sets gap 0, so this row needs its own breathing room under the box.
.location-editor__url-row--fb {
  margin-top: 7px;
}
.location-editor__social-logo {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  margin-top: 8px;
}
.location-editor__social-body {
  display: grid;
  gap: 7px;
  flex: 1;
  min-width: 0;
}
.location-editor__discovery-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #667085;
  font-weight: 400;
}
.location-editor__handle-input {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  border: 1px solid #cbd0d7;
  border-radius: 6px;
  overflow: hidden;
  background: white;
}
.location-editor__url-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.location-editor__url-link:hover {
  text-decoration: underline;
}
.location-editor__handle-input span {
  padding: 10px 12px;
  border-right: 1px solid #d8dce2;
  color: #667085;
}
.location-editor__handle-input input {
  flex: 1;
  min-width: 0;
  padding: 10px;
  border: 0;
  outline: 0;
}
.location-editor__inline-loader {
  flex: 0 0 auto;
  margin-right: 10px;
  color: #667085;
}
.location-editor__status-row {
  margin: 0;
  font-size: 14px;
  line-height: 18px;
}
.location-editor__valid {
  color: #21883f;
  font-weight: 600;
}
.location-editor__instagram-guidance {
  color: #ec573b;
  font-weight: 600;
}
.location-editor__fb-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 0;
  padding: 4px;
  border-radius: 8px;
  background: #f2f4f7;
}
.location-editor__fb-tab {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #667085;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.12s ease,
    color 0.12s ease;
}
.location-editor__fb-tab:hover:not(.location-editor__fb-tab--active) {
  color: #344054;
}
.location-editor__fb-tab--active {
  border-color: #d8dce2;
  background: #fff;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
}
.location-editor__fb-panel {
  border: 1px solid #d8dce2;
  border-radius: 3px;
  background: #fff;
  overflow: hidden;
}
.location-editor__fb-panel:focus-within {
  border-color: #118689;
}
.location-editor__fb-panel .location-editor__fb-search-input:focus {
  border: 0;
}
.location-editor__fb-dropdown {
  position: fixed;
  z-index: 1300;
  max-height: 320px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid #d8dce2;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 5px 20px rgba(16, 24, 40, 0.16);
}
.location-editor__fb-search-input {
  width: 100%;
  padding: 11px 14px;
  border: 0;
  outline: 0;
  font: inherit;
  font-size: 14px;
  font-weight: 400;
}
.location-editor__fb-search-input::placeholder {
  color: #98a2b3;
}
.location-editor__fb-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}
.location-editor__fb-status--bare {
  padding: 0;
  border-top: 0;
}
.location-editor__fb-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  border: 0;
  border-top: 1px solid #e7e9ed;
  background: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.location-editor__fb-result:first-child {
  border-top: 0;
}
.location-editor__fb-result:hover,
.location-editor__fb-result--active {
  background: #eef6f5;
}
.location-editor__fb-result:focus-visible {
  outline: 2px solid #118689;
  outline-offset: -2px;
}
.location-editor__fb-result-info {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.location-editor__fb-name {
  color: #101828;
  font-size: 15px;
  font-weight: 600;
}
.location-editor__fb-handle {
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}
.location-editor__fb-result-info small {
  color: #667085;
  font-size: 13px;
  font-weight: 400;
}
.location-editor__fb-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #d8dce2;
  border-radius: 8px;
  background: #fff;
}
.location-editor__fb-selected-name {
  min-width: 0;
  color: #101828e0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.location-editor__fb-change {
  flex: 0 0 auto;
  padding: 4px;
  border: 0;
  background: transparent;
  color: #667085;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}
.location-editor__fb-change:hover {
  color: #101828;
}
.location-editor__fb-error {
  margin: 7px 0 0;
}
</style>

<style lang="scss">
// Google appends the autocomplete dropdown to the body at z-index 1000, which is below the modals this form sits in.
.pac-container {
  z-index: 1300;
}
</style>
